import React from 'react';
import { Alert, Box, Chip, LinearProgress, Typography } from '@mui/material';

const STATUS_COLOR = {
  pushed: 'success',
  queued: 'info',
  skipped: 'default',
  failed: 'error',
};

export default function RateUpdateProgressPanel({
  phase = 'idle',
  total = 0,
  result = null,
  pmsOnly = false,
}) {
  if (phase === 'idle') return null;

  if (phase === 'saving') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          {pmsOnly
            ? `Saving ${total} PMS rate row${total === 1 ? '' : 's'}…`
            : `Saving ${total} rate row${total === 1 ? '' : 's'} to PMS and Channel Manager…`}
        </Typography>
        <LinearProgress />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {pmsOnly
            ? 'PMS database only. OTAs are not updated.'
            : 'OTA push first. Rates are saved after the channels accept the update.'}
        </Typography>
      </Box>
    );
  }

  if (!result) return null;

  const saved = result.successful ?? 0;
  const failed = result.failed ?? 0;
  const skipped = result.skipped ?? 0;
  const ota = result.ota || {};
  const channels = ota.channels || [];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
        Database
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        {saved} saved
        {skipped ? ` · ${skipped} skipped` : ''}
        {failed ? ` · ${failed} failed` : ''}
        {` · ${result.total ?? total} total`}
      </Typography>

      {!pmsOnly && (
      <>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
        OTA push
      </Typography>
      {ota.attempted ? (
        <Alert
          severity={ota.success ? 'success' : 'error'}
          sx={{ mb: 1, py: 0.25 }}
        >
          {ota.success
            ? (ota.message || `Pushed ${ota.rates_pushed || 0} rate segment(s)`)
            : (ota.error || 'OTA rate push failed')}
        </Alert>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {ota.message || 'No active OTA mappings to push'}
        </Typography>
      )}
      {channels.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {channels.map((channel) => (
            <Chip
              key={`${channel.id}-${channel.name}`}
              size="small"
              color={STATUS_COLOR[channel.status] || 'default'}
              variant={channel.status === 'skipped' ? 'outlined' : 'filled'}
              label={
                channel.reason
                  ? `${channel.name}: ${channel.status} — ${channel.reason}`
                  : `${channel.name}: ${channel.status}`
              }
            />
          ))}
        </Box>
      ) : null}
      </>
      )}
    </Box>
  );
}
